import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { UserPlus, Trash2, Loader2, X, Shield, Edit3, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminApi, adminMe } from "@/lib/adminApi";
import AdminShell from "@/components/admin/AdminShell";

const ROLE_BADGE = {
  admin: { label: "Admin", cls: "bg-[#0E2A57] text-white" },
  editor: { label: "Editor", cls: "bg-[#BEE7F5] text-[#0E2A57]" },
};

// All available permission scopes — must mirror backend ALLOWED_PERMISSIONS
const PERMISSION_GROUPS = [
  {
    label: "Core",
    items: [
      { key: "pages", desc: "Create / edit / delete pages" },
      { key: "services", desc: "Manage Services CRUD" },
      { key: "blog", desc: "Write & publish blog posts" },
      { key: "hiring", desc: "Manage job openings" },
      { key: "media", desc: "Upload / delete media assets" },
      { key: "settings", desc: "Edit brand, nav, footer, contact" },
    ],
  },
  {
    label: "Content collections",
    items: [
      { key: "testimonials", desc: "Client testimonials" },
      { key: "faqs", desc: "FAQ entries" },
      { key: "team", desc: "Team members" },
      { key: "case-studies", desc: "Case studies" },
      { key: "kpis", desc: "KPI / stats" },
      { key: "sectors", desc: "Sectors & industries" },
      { key: "banners", desc: "Site banners" },
      { key: "partners", desc: "Partners & client logos" },
      { key: "pricing-plans", desc: "Pricing tiers" },
      { key: "locations", desc: "Office locations" },
      { key: "documents", desc: "Downloadable documents" },
    ],
  },
  {
    label: "System",
    items: [
      { key: "subscribers", desc: "Newsletter subscriber list" },
      { key: "redirects", desc: "URL redirects" },
    ],
  },
];

const ALL_PERMS = PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.key));

export default function AdminUsers() {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", role: "editor", permissions: ["pages", "services", "blog", "media"] });
  const [editingPerms, setEditingPerms] = useState(null); // { email, permissions }

  const load = async () => {
    setLoading(true);
    try {
      const [meData, usersData] = await Promise.all([
        adminMe(),
        adminApi.get("/admin/accounts").then((r) => r.data),
      ]);
      setMe(meData);
      setUsers(usersData);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin role required");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!loading && me && me.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/admin/accounts", newUser);
      toast.success("User created");
      setCreating(false);
      setNewUser({ email: "", password: "", role: "editor", permissions: ["pages", "services", "blog", "media"] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };

  const updateRole = async (email, role) => {
    try {
      await adminApi.patch(`/admin/accounts/${encodeURIComponent(email)}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const savePermissions = async () => {
    try {
      await adminApi.patch(
        `/admin/accounts/${encodeURIComponent(editingPerms.email)}`,
        { permissions: editingPerms.permissions }
      );
      toast.success("Permissions updated");
      setEditingPerms(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const togglePerm = (key) => {
    const set = new Set(editingPerms.permissions);
    set.has(key) ? set.delete(key) : set.add(key);
    setEditingPerms({ ...editingPerms, permissions: Array.from(set) });
  };

  const setAllPerms = (all) => {
    setEditingPerms({ ...editingPerms, permissions: all ? [...ALL_PERMS] : [] });
  };

  const updatePassword = async (email) => {
    const newPassword = window.prompt(`Set a new password for ${email} (min 6 chars):`);
    if (!newPassword || newPassword.length < 6) return;
    try {
      await adminApi.patch(`/admin/accounts/${encodeURIComponent(email)}`, { password: newPassword });
      toast.success("Password updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"? Cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/accounts/${encodeURIComponent(u.email)}`);
      toast.success("User deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <AdminShell title="Users & Roles">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ags-text-muted">Manage admin accounts. Admins have full access; Editors can edit content but not manage users or delete pages.</p>
        <button onClick={() => setCreating(true)} data-testid="new-user-btn"
                className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]">
          <UserPlus size={14} /> New User
        </button>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-[#050B14]/60 z-50 flex items-start justify-center pt-24 px-4">
          <form onSubmit={create} className="bg-white w-full max-w-md border border-[#0E2A57]/15">
            <div className="px-6 py-4 border-b border-ags-hairline flex items-center justify-between">
              <h2 className="font-heading font-bold">New User</h2>
              <button type="button" onClick={() => setCreating(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">Email</span>
                <input type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                       className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
              </label>
              <label className="block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">Password (min 6 chars)</span>
                <input type="password" required minLength={6} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                       className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
              </label>
              <label className="block">
                <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">Role</span>
                <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                        className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2">
                  <option value="editor">Editor — can edit content</option>
                  <option value="admin">Admin — full access</option>
                </select>
              </label>
            </div>
            <div className="px-6 py-4 border-t border-ags-hairline flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-[#0E2A57]/30 font-mono text-[10px] uppercase tracking-[0.22em]">Cancel</button>
              <button type="submit" className="bg-[#0E2A57] text-white px-5 py-2 font-mono text-[10px] uppercase tracking-[0.22em]">Create</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-[#0E2A57]/15">
        {loading ? <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div> :
         <ul className="divide-y divide-ags-hairline">
           {users.map((u) => {
             const isMe = me?.email === u.email;
             const role = u.role || "admin";
             const badge = ROLE_BADGE[role] || ROLE_BADGE.admin;
             return (
               <li key={u.email} className="px-6 py-4 flex items-center gap-4">
                 <div className="w-10 h-10 bg-ags-cream flex items-center justify-center font-heading font-bold text-[#0E2A57]">
                   {(u.email || "?")[0].toUpperCase()}
                 </div>
                 <div className="flex-1 min-w-0">
                   <div className="font-heading font-bold text-ags-ink tracking-[-0.02em]">
                     {u.email} {isMe && <span className="ml-2 text-xs font-mono text-[#2BB6E5]">(you)</span>}
                   </div>
                   <div className="font-mono text-xs text-ags-text-muted mt-1">Created {(u.created_at || "").slice(0, 10)}</div>
                 </div>
                 <span className={`inline-flex items-center gap-1.5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${badge.cls}`}>
                   <Shield size={10} /> {badge.label}
                 </span>
                 <button onClick={() => updateRole(u.email, role === "admin" ? "editor" : "admin")}
                         disabled={isMe} title="Toggle role"
                         className="inline-flex items-center gap-1 border border-[#0E2A57]/30 hover:bg-ags-cream disabled:opacity-30 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em]">
                   <Edit3 size={11} /> Toggle Role
                 </button>
                 {role === "editor" && (
                   <button
                     onClick={() => setEditingPerms({ email: u.email, permissions: u.permissions || [] })}
                     data-testid={`perms-btn-${u.email}`}
                     title="Granular permissions"
                     className="inline-flex items-center gap-1 border border-[#2BB6E5] text-[#0E2A57] hover:bg-[#BEE7F5]/30 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em]"
                   >
                     <KeyRound size={11} /> Perms ({(u.permissions || []).length})
                   </button>
                 )}
                 <button onClick={() => updatePassword(u.email)} title="Reset password"
                         className="inline-flex items-center gap-1 border border-[#0E2A57]/30 hover:bg-ags-cream px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em]">
                   Reset Pw
                 </button>
                 <button onClick={() => remove(u)} disabled={isMe}
                         className="text-red-600 hover:bg-red-50 disabled:opacity-30 p-2"><Trash2 size={16} /></button>
               </li>
             );
           })}
         </ul>
        }
      </div>

      {/* Granular permissions drawer */}
      {editingPerms && (
        <div className="fixed inset-0 z-50 flex" data-testid="perms-editor">
          <div className="flex-1 bg-black/40" onClick={() => setEditingPerms(null)} />
          <div className="w-full max-w-xl bg-white shadow-2xl h-full overflow-y-auto">
            <div className="px-7 py-5 border-b border-ags-hairline flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">Permissions</div>
                <h2 className="mt-1 font-heading font-extrabold text-xl tracking-[-0.02em] text-ags-ink">
                  {editingPerms.email}
                </h2>
              </div>
              <button onClick={() => setEditingPerms(null)} className="text-ags-text-muted hover:text-ags-ink">
                <X size={18} />
              </button>
            </div>

            <div className="px-7 py-4 border-b border-ags-hairline flex items-center justify-between bg-ags-cream">
              <span className="text-sm text-ags-text-muted">
                {editingPerms.permissions.length} of {ALL_PERMS.length} scopes granted
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setAllPerms(true)}
                  data-testid="perms-all"
                  className="border border-[#0E2A57]/25 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em]"
                >
                  Grant all
                </button>
                <button
                  onClick={() => setAllPerms(false)}
                  data-testid="perms-none"
                  className="border border-red-300 text-red-700 hover:bg-red-700 hover:text-white px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em]"
                >
                  Revoke all
                </button>
              </div>
            </div>

            <div className="px-7 py-6 space-y-6">
              {PERMISSION_GROUPS.map((g) => (
                <div key={g.label}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55 mb-3">{g.label}</div>
                  <ul className="divide-y divide-ags-hairline border border-ags-hairline">
                    {g.items.map((p) => {
                      const checked = editingPerms.permissions.includes(p.key);
                      return (
                        <li key={p.key} className="px-4 py-3 flex items-center gap-3 hover:bg-ags-cream">
                          <input
                            type="checkbox"
                            checked={checked}
                            data-testid={`perm-${p.key}`}
                            onChange={() => togglePerm(p.key)}
                            className="w-4 h-4 accent-[#0E2A57]"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-xs uppercase tracking-[0.16em] text-ags-ink">{p.key}</div>
                            <div className="text-xs text-ags-text-muted mt-0.5">{p.desc}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <div className="px-7 py-5 border-t border-ags-hairline flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditingPerms(null)}
                className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted hover:text-ags-ink"
              >
                Cancel
              </button>
              <button
                onClick={savePermissions}
                data-testid="perms-save"
                className="bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                Save permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
